<template>
  <div>
    <channel-field class="mb-3">
      <div slot="label">Facebook Page:</div>
      <div slot="value">{{ channel.facebook_page.name }}</div>
    </channel-field>
    <channel-field class="mb-3">
      <div slot="label">Facebook Page ID:</div>
      <div slot="value">{{ channel.facebook_page.id }}</div>
    </channel-field>
    <channel-field class="mb-3">
      <div slot="label">Category:</div>
      <div slot="value">{{ channel.facebook_page.category }}</div>
    </channel-field>
    <channel-field class="mb-3">
      <div slot="label">
        Facebook App:
        <span id="facebook_app_info" v-b-popover:facebook_app_info.html.hover.right="appInfoText">
          <icon name="info-circle"></icon>
        </span>
      </div>
      <div slot="value">{{ facebookApp.name }} (APP ID: {{ facebookApp.id }})</div>
    </channel-field>
    <channel-field v-show="showPlugin" class="mb-3">
      <div slot="label">Test Connection:</div>
      <div slot="value">
        <plugin-send2messenger :key="pluginKey"
                               :facebookPageId="channel.facebook_page.id"
                               :facebookPageName="channel.facebook_page.name"
                               :testRef="sendToMessengerTestRef"
                               :channelId="channel.id"></plugin-send2messenger>
      </div>
    </channel-field>
    <div v-if="!editMode">
      <button type="button" class="btn btn-sm btn-primary" @click="reauthorizePromise = reauthorize()" v-busy-button="reauthorizePromise">
        Re-authorize Facebook Page
      </button>
    </div>
  </div>
</template>

<script>
  /* eslint-disable curly */
  /* eslint-disable vue/script-indent */

  import ChannelDetailField from '@/components/administration/common/Field'
  import FbSendToMessenger from './FbSendToMessenger'
  import channelService from '@/services/amio-api/channel.service'
  import facebookService from '@/services/facebook/facebook.service'
  import facebookPermission from '@/services/facebook/facebook-permission.enum'
  import eventBus from '@/services/event-bus/event-bus'
  import notificationService from '@/services/notification/notification.service'

  const managePagesErrorText = 'This Facebook Page lacks permission to be managed by Amio. You can fix this by using ' +
    'the <b>Re-authorize Facebook Page</b> button below, and granting the permission for this Facebook Page.'
  const invalidAccessTokenErrorText = 'Amio is unable to connect to this Facebook Page. Please re-authorize the Page ' +
    'by using the <b>Re-authorize Facebook Page</b> button below.'

  export default {
    components: {
      'channel-field': ChannelDetailField,
      'plugin-send2messenger': FbSendToMessenger
    },

    store: {
      organization: '_common.organization',
      channel: 'administration.channelDetail.channel'
    },

    props: [
      'editMode'
    ],

    data() {
      return {
        sendToMessengerTestRef: process.env.VUE_APP_TEST_REF_SEND_TO_MESSENGER,
        reauthorizePromise: null,
        pluginKey: 0,
        showPlugin: true,
        managePagesErrorText,
        invalidAccessTokenErrorText
      }
    },

    computed: {
      appInfoText() {
        const defaultAppInfo = 'You can use a custom app here. Contact us for more details or visit <a href="https://docs.amio.io/docs/facebook-messenger-tutorials-custom-facebook-app" target="_blank">documentation.</a>'
        const customAppInfo = 'This is your custom app ID. For more details, visit <a href="https://docs.amio.io/docs/facebook-messenger-tutorials-custom-facebook-app" target="_blank">documentation.</a>'

        return this.organization.facebook_app ? customAppInfo : defaultAppInfo
      },
      facebookApp() {
        if (this.organization.facebook_app) {
          return {
            name: this.organization.facebook_app.name,
            id: this.organization.facebook_app.id
          }
        }

        return {
          name: 'Amio',
          id: process.env.VUE_APP_FACEBOOK_APP_ID
        }
      }
    },

    methods: {
      checkPermissions() {
        return facebookService.checkPermissions(this.channel.facebook_page)
          .then((errorCode) => {
            const alertEvent = this.$enums.alerts.events.channelInfo
            const typeDanger = this.$enums.alerts.types.danger

            switch (errorCode) {
              case facebookPermission.OK:
                this.$emit(alertEvent, false)
                this.showPlugin = true
                return
              case facebookPermission.MANAGE_PAGES_NOT_ALLOWED:
                // in this case it's enough to just show FB ui and enable the page to be connected to the app
                this.$emit(alertEvent, true, typeDanger, managePagesErrorText)
                this.showPlugin = false
                return
              case facebookPermission.INVALID_ACCESS_TOKEN:
              case facebookPermission.GENERIC_ERROR:
              default:
                // we need a new access token
                this.$emit(alertEvent, true, typeDanger, invalidAccessTokenErrorText)
                this.showPlugin = false
            }
          })
      },
      reauthorize() {
        return new Promise(resolve => {
          return facebookService.getLoginStatus()
            .then(loginStatus => {
              if (loginStatus === 'connected') return Promise.resolve()

              return facebookService.login()
            })
            .then(() => this.updateAccessToken())
            .then(resolve) // stop the busy button
            .catch(err => {
              notificationService.error(JSON.stringify(err))
            })
        })
      },
      updateAccessToken() {
        return facebookService.getPageInfo(this.channel.facebook_page.id)
          .then((pageInfo) => {
            const accessToken = pageInfo.access_token
            return channelService.patch(this.channel.id, {facebook_page: {access_token: accessToken}}, [422])
          })
          .then((response) => {
            eventBus.$emit('update-channel', response.data)
            return this.checkPermissions()
          })
          .then(() => {
            // changing the component's key tells Vue to rebuild the component
            // this is needed to re-initialize send2messenger plugin
            this.pluginKey += 1
          })
          .catch(() => {
            notificationService.error('Authorization failed')
          })
      }
    },

    mounted() {
      this.checkPermissions()
    }
  }
</script>
